import api from "../../Hooks/axiosApi";



// ---------- Screen 1: Members list ----------
export const getOperatorCompaniesService = (params: {
    searchText?: string;
    isActive?: boolean;
}) => {
    return api.get("/api/platform/operator/companies", { params });
};

// ---------- Screen 2: Member detail ----------
export const getOperatorCompanyByIdService = (companyId: number | string) => {
    return api.get(`/api/platform/operator/companies/${companyId}`);
};

// Optional helper if you need to refresh just the staff table after a toggle
export const getOperatorCompanyStaffService = (companyId: number | string) => {
    return api.get(`/api/platform/operator/companies/${companyId}/staff`);
};

export const updateOperatorCompanyStatusService = ({
    companyId,
    isActive,
}: {
    companyId: number | string;
    isActive: boolean;
}) => {
    return api.put(`/api/platform/operator/companies/${companyId}/status`, null, {
        params: { isActive },
    });
};

export const updateOperatorStaffStatusService = ({
    staffId,
    isActive,
}: {
    staffId: number | string;
    isActive: boolean;
}) => {
    return api.put(`/api/platform/operator/staff/${staffId}/status`, null, {
        params: { isActive },
    });
};

// ---------- Screen 3: Create member ----------
export const createOperatorMemberService = (data: {
    businessName: string;
    acn?: string;
    abn?: string;
    contactName?: string;
    contactMobile?: string;
    contactEmail?: string;
    bookingLink?: string;
    brandColorPrimary?: string;
    brandColorSecondary?: string;
    firstName: string;
    surname: string;
    mobile?: string;
    email: string;
    isActive?: boolean;
}) => {
    return api.post("/api/platform/operator/members", data);
};

// ---------- Fintrack edits a member's details ----------
// The CRM controls a member's details; the member's own Platform screen writes the
// same record, so a change made in either place shows in both.
export const updateOperatorCompanyService = (
    companyId: number | string,
    data: {
        businessName: string;
        shortName?: string | null;
        acn?: string | null;
        abn?: string | null;
        contactName?: string | null;
        contactMobile?: string | null;
        contactEmail?: string | null;
        bookingLink?: string | null;
        brandColorPrimary?: string | null;
        brandColorSecondary?: string | null;
    }
) => {
    return api.put(`/api/platform/operator/companies/${companyId}`, data);
};

export const uploadOperatorCompanyLogoService = (companyId: number | string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post(`/api/platform/operator/companies/${companyId}/logo`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
};

export const uploadOperatorCompanyPhotoService = (companyId: number | string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post(`/api/platform/operator/companies/${companyId}/photo`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
};

// ---------- Fintrack manages a member's staff ----------
type OperatorStaffBody = { firstName: string; surname: string; mobile?: string | null; email: string };

export const addOperatorStaffService = (companyId: number | string, data: OperatorStaffBody) => {
    return api.post(`/api/platform/operator/companies/${companyId}/staff`, data);
};

export const updateOperatorStaffService = (companyId: number | string, staffId: number | string, data: OperatorStaffBody) => {
    return api.put(`/api/platform/operator/companies/${companyId}/staff/${staffId}`, data);
};

export const deleteOperatorStaffService = (companyId: number | string, staffId: number | string) => {
    return api.delete(`/api/platform/operator/companies/${companyId}/staff/${staffId}`);
};

// Fintrack admin logging in AS a member. Returns { url } - a link that opens the
// Platform as that member's owner, valid for two hours.
export const loginAsOperatorCompanyService = (companyId: number | string) => {
    return api.post(`/api/platform/operator/companies/${companyId}/login-as`);
};

// ---------- Screen 4: EOIs ----------
export const getOperatorEoisService = (params: {
    fromDate?: string;
    toDate?: string;
    companyId?: number | string;
}) => {
    return api.get("/api/platform/operator/eois", { params });
};

// ---------- Usage counts (used behind a date-range picker on Screen 2) ----------
export const getOperatorUsageService = (
    companyId: number | string,
    params: { fromDate?: string; toDate?: string }
) => {
    return api.get(`/api/platform/operator/usage/${companyId}`, { params });
};
// ---------- StateName in the searches table ----------

export const getPlatformStatesService = () => api.get("/api/platform/suburbs/states");


// ---------- Operator Company Search (searches table below EOIs on Member Detail) ----------
export const getOperatorSearchesService = (params: {
    companyId?: number | string;
    fromDate?: string;
    toDate?: string;
    pageNumber?: number;
    pageSize?: number;
}) => {
    return api.get("/api/platform/operator/searches", { params });
};

// ---------- Optional admin tools ----------
export const sendOperatorTestEmailService = (to: string) => {
    return api.post("/api/platform/operator/test-email", null, { params: { to } });
};

export const getOperatorHealthReadyService = () => {
    return api.get("/api/platform/health/ready");
};

// ---------- Services and fees on a member ----------
export const getOperatorCompanyServicesService = (companyId: number | string) => {
    return api.get(`/api/platform/operator/companies/${companyId}/services`);
};

export const saveOperatorCompanyServiceService = (payload: {
    companyId: number | string;
    serviceCode: string;
    isActive: boolean;
    startDate?: string | null;
    monthlyFee?: number | null;
    commissionIncluded?: boolean | null;
    notes?: string | null;
}) => {
    const { companyId, ...body } = payload;
    return api.put(`/api/platform/operator/companies/${companyId}/services`, body);
};

// ---------- A member's own client list ----------
export const getOperatorCompanyLeadsService = (companyId: number | string, params?: { staffId?: number; searchText?: string }) => {
    return api.get(`/api/platform/operator/companies/${companyId}/leads`, { params });
};

export const importOperatorCompanyLeadsService = (companyId: number | string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post(`/api/platform/operator/companies/${companyId}/leads/import`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
};

// ---------- Sonia's knowledge base on a member ----------
// Fintrack writes this; the business only reads it in their own app. That is
// the SONIA_KB service they are paying for.
export const getOperatorCompanyKnowledgeService = (companyId: number | string) => {
    return api.get(`/api/platform/operator/companies/${companyId}/knowledge`);
};

export const saveOperatorCompanyKnowledgeService = (
    companyId: number | string,
    body: {
        knowledgeId?: number;
        category?: string;
        title: string;
        content: string;
        sortOrder?: number;
        isActive?: boolean;
    }
) => {
    return api.put(`/api/platform/operator/companies/${companyId}/knowledge`, body);
};

export const deleteOperatorCompanyKnowledgeService = (companyId: number | string, knowledgeId: number) => {
    return api.delete(`/api/platform/operator/companies/${companyId}/knowledge/${knowledgeId}`);
};

export const saveOperatorCompanySoniaService = (
    companyId: number | string,
    body: {
        businessIntro?: string | null;
        tone?: string | null;
        officeHours?: string | null;
        servicesOffered?: string | null;
        doNotSay?: string | null;
        bookingInstructions?: string | null;
        escalationEmail?: string | null;
    }
) => {
    return api.put(`/api/platform/operator/companies/${companyId}/sonia`, body);
};

// ---------- Billing and margin (Fintrack only) ----------
// What each client is billed, what they cost us, and what is left. The API
// refuses these routes to anyone but an Operator, so no member can reach them.
export const getBillingSummaryService = (params: { year: number; month: number }) => {
    return api.get(`/api/platform/operator/billing`, { params });
};

export const getBillingCompanyService = (companyId: number | string, params: { year: number; month: number; months?: number }) => {
    return api.get(`/api/platform/operator/billing/companies/${companyId}`, { params });
};

export const getBillingPricingService = () => {
    return api.get(`/api/platform/operator/billing/pricing`);
};

export const saveBillingPricingService = (body: {
    serviceCode?: string;
    standardMonthlyFee?: number | null;
    monthlyCost?: number | null;
    messageChargePer100?: number | null;
    messageCostPer100?: number | null;
}) => {
    return api.put(`/api/platform/operator/billing/pricing`, body);
};

export const saveBillingUsageService = (body: {
    companyId: number;
    periodYear: number;
    periodMonth: number;
    messagesSent: number;
    notes?: string | null;
}) => {
    return api.put(`/api/platform/operator/billing/usage`, body);
};

// ---------- the month's invoices ----------
// Richard, 3 Oct 2026: the run is built here, he approves it, and only then does
// anything reach Xero. GET is read-only; approve is the only call that writes.
export const getInvoiceRunService = (params: { year: number; month: number }) => {
    return api.get(`/api/platform/operator/billing/invoices`, { params });
};

export const approveInvoicesService = (body: {
    periodYear: number;
    periodMonth: number;
    companyIds: number[];
}) => {
    return api.post(`/api/platform/operator/billing/invoices/approve`, body);
};

// The price list is Richard's to run: add a service, rename one, reprice it, or stop
// offering it. Only the fields sent are written, so saving one box leaves the rest alone.
export const saveBillingServiceService = (body: {
    serviceCode: string;
    serviceName?: string;
    standardMonthlyFee?: number | null;
    monthlyCost?: number | null;
    sortOrder?: number | null;
    isActive?: boolean | null;
    includedUnits?: number | null;
    unitLabel?: string | null;
    perUnitFee?: number | null;
}) => {
    return api.post(`/api/platform/operator/billing/service`, body);
};

// Gameplan usage is counted in the SMS engine's own database, so a client has to be
// tied to a company there before their numbers can be shown. Where both carry the same
// ABN that happens by itself; these two are for the ones that do not.

export const getSoniaCompaniesService = () => {
    return api.get(`/api/platform/operator/billing/sonia-companies`);
};

export const setSoniaLinkService = (body: {
    companyId: number;
    soniaCompanyId: number | null;
}) => {
    return api.post(`/api/platform/operator/billing/sonia-link`, body);
};

export const exportBillingCsvService = (params: { year: number; month: number }) => {
    return api.get(`/api/platform/operator/billing/export`, { params, responseType: 'blob' });
};

// ---------- Sonia coaching reports (Fintrack only) ----------
// The master scorecard across every client, then any one client in full.
// Operator-gated in the API, so a member token cannot reach any of it.
export const getSoniaReportAllService = (params: { year: number; month: number }) => {
    return api.get(`/api/platform/operator/sonia/report`, { params });
};

export const getSoniaReportCompanyService = (
    companyId: number | string,
    params: { year: number; month: number; staffId?: number },
) => {
    return api.get(`/api/platform/operator/sonia/report/${companyId}`, { params });
};

// ---------- Sonia's scoreboard, straight from the SMS engine ----------
// The dashboards do not recount what she already counts. The counts come out of her
// own database and the three numbers only a broker can know - appointments attended,
// loans written, settled lending - come from what the broker typed in.
export const getSoniaScoreboardService = (params: { audience: 'brokers' | 'clients'; period?: string; months?: number }) => {
    return api.get(`/api/platform/operator/sonia/scoreboard`, { params });
};

//  ---------- the coaching half of the same page ----------
//  The counts are what she did. These three are the conversation about them: what the
//  owner asked, what was agreed, and what has been finished. An agreed action stays on
//  the page every month until somebody ticks it, which is the whole point of writing it
//  down rather than mentioning it.
export const askSoniaScoreboardService = (body: {
    audience: 'brokers' | 'clients';
    period?: string;
    message: string;
    history?: { role: string; content: string }[];
}) => {
    return api.post(`/api/platform/operator/sonia/scoreboard/ask`, body);
};

export const addSoniaScoreboardActionService = (body: {
    audience: 'brokers' | 'clients';
    owner?: string;
    title: string;
    detail?: string;
    due?: string;
}) => {
    return api.post(`/api/platform/operator/sonia/scoreboard/action`, body);
};

export const doneSoniaScoreboardActionService = (actionId: number) => {
    return api.post(`/api/platform/operator/sonia/scoreboard/action/${actionId}/done`, {});
};

export const exportSoniaReportCsvService = (params: { year: number; month: number }) => {
    return api.get(`/api/platform/operator/sonia/report/export`, { params, responseType: 'blob' });
};

// Until the SMS side writes its own figures, the month is typed in here.
export const saveSoniaActivityService = (body: {
    companyId: number;
    staffId?: number | null;
    campaign?: string;
    periodYear: number;
    periodMonth: number;
    messagesSent?: number;
    replies?: number;
    conversations?: number;
    qualified?: number;
    booked?: number;
    showedUp?: number;
    settled?: number;
    escalations?: number;
    optOuts?: number;
}) => {
    return api.post(`/api/platform/operator/sonia/activity`, body);
};

// ---------- The knowledge base loop ----------
// What Sonia was asked and could not answer. Answer it here and it becomes a
// knowledge base entry for that client, which is what makes the base self-updating.
export const getSoniaQuestionsService = (companyId: number | string, params?: { status?: string }) => {
    return api.get(`/api/platform/operator/sonia/questions/${companyId}`, { params });
};

export const saveSoniaQuestionService = (body: {
    questionId?: number;
    companyId: number;
    questionText: string;
    suggestedAnswer?: string | null;
}) => {
    return api.post(`/api/platform/operator/sonia/questions`, body);
};

export const resolveSoniaQuestionService = (body: {
    questionId: number;
    companyId: number;
    action: "Add" | "Ignore" | "Reopen";
    category?: string;
    answer?: string | null;
}) => {
    return api.post(`/api/platform/operator/sonia/questions/resolve`, body);
};

// ---------- Follow-up sequences (Fintrack only) ----------
// A sequence is a ladder of messages, SMS and email mixed, each with a day and
// an hour after a lead goes on it. Fintrack builds and maintains them.
export const getSequencesService = (params: { companyId: number; includeTemplates?: boolean }) => {
    return api.get(`/api/platform/operator/sequences`, { params });
};

export const getSequenceService = (sequenceId: number) => {
    return api.get(`/api/platform/operator/sequences/${sequenceId}`);
};

export const saveSequenceService = (body: {
    sequenceId?: number;
    companyId: number;
    name: string;
    campaign?: string;
    description?: string | null;
    isActive?: boolean;
}) => {
    return api.post(`/api/platform/operator/sequences`, body);
};

export const copySequenceService = (body: { sequenceId: number; companyId: number; name?: string }) => {
    return api.post(`/api/platform/operator/sequences/copy`, body);
};

export const saveSequenceStepService = (body: {
    stepId?: number;
    sequenceId: number;
    stepNo: number;
    dayOffset: number;
    sendHour?: number | null;
    channel: "SMS" | "Email";
    subject?: string | null;
    body: string;
    isActive?: boolean;
}) => {
    return api.post(`/api/platform/operator/sequences/steps`, body);
};

export const deleteSequenceStepService = (stepId: number, sequenceId: number) => {
    return api.delete(`/api/platform/operator/sequences/steps/${stepId}`, { params: { sequenceId } });
};

export const getSequenceMergeFieldsService = () => {
    return api.get(`/api/platform/operator/sequences/merge-fields`);
};

export const enrolLeadsService = (body: {
    companyId: number;
    sequenceId: number;
    companyLeadIds: number[];
    startOn?: string | null;
}) => {
    return api.post(`/api/platform/operator/sequences/enrol-many`, body);
};

export const stopSequenceForLeadService = (body: { companyLeadId: number; reason: string; sequenceId?: number }) => {
    return api.post(`/api/platform/operator/sequences/stop`, body);
};

export const getSequenceLeadStatusService = (companyLeadId: number) => {
    return api.get(`/api/platform/operator/sequences/leads/${companyLeadId}`);
};

// What is due to go out now, already merged and checked.
export const getSequenceDueService = (params?: { top?: number; companyId?: number }) => {
    return api.get(`/api/platform/operator/sequences/due`, { params });
};

export const getSequenceSummaryService = (params?: { companyId?: number }) => {
    return api.get(`/api/platform/operator/sequences/summary`, { params });
};

// ---------- The sender (Fintrack only) ----------
// Two switches: a master one that stops everything, and one per client. Both
// off by default, so nothing sends until somebody deliberately turns it on.
export const getSendSettingsService = (params?: { companyId?: number }) => {
    return api.get(`/api/platform/operator/send/settings`, { params });
};

export const saveSendSettingsService = (body: {
    companyId: number;
    sendingOn: boolean;
    earliestHour: number;
    latestHour: number;
    sendOnSunday: boolean;
    dailyCap: number;
    stateCode?: string | null;
}) => {
    return api.put(`/api/platform/operator/send/settings`, body);
};

export const saveSendSwitchService = (body: { sendingOn: boolean; reason?: string | null }) => {
    return api.put(`/api/platform/operator/send/switch`, body);
};

export const getSendHealthService = () => {
    return api.get(`/api/platform/operator/send/health`);
};

// Claims a real batch, shows it merged and checked, and sends nothing.
export const sendDryRunService = (body?: { top?: number; companyId?: number }) => {
    return api.post(`/api/platform/operator/send/dry-run`, body ?? {});
};

export const sendRunService = (body?: { top?: number; companyId?: number }) => {
    return api.post(`/api/platform/operator/send/run`, body ?? {});
};

// ---------- Robert Johns, the robot tester ----------
// Is he testing, and his last runs with every line of the checklist. Read only.
export const getRobertService = () => {
    return api.get(`/api/platform/operator/robert`);
};

// Test now. Starts a test; nothing in any system is changed by it.
export const runRobertService = () => {
    return api.post(`/api/platform/operator/robert/run`, {});
};
